import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * SAB-884: lets a route declare "I own the whole screen", so the site chrome rendered
 * OUTSIDE the router can step aside.
 *
 * ── WHY A CONTEXT AND NOT A PROP ────────────────────────────────────────────
 *
 * `<Footer />` is a SIBLING of `<Routes>` in App.tsx. No prop reaches it from inside a
 * route, so a route that needs the whole viewport has no way to say so.
 *
 * ── WHY NOT GATE ON THE URL, WHICH IS THE OBVIOUS ANSWER ────────────────────
 *
 * Footer already self-gates on `pathname === '/'`, so matching that shape is tempting. It
 * cannot work here: NFLStaging holds the active tab in STATE
 * (`useState(searchParams.get('tab') || 'home')`) and an in-app tab click only calls
 * `setActiveTab` -- no navigation. `/nfl` showing the draft room is byte-identical by URL to
 * `/nfl` showing Home. A pathname gate would either strip the footer from every NFL tab --
 * including the ones where its legal and cookie-preference links are compliance surface --
 * or miss the draft room whenever it is reached by tapping rather than by deep link.
 *
 * So the signal is runtime and declared by the room itself.
 *
 * ── A COUNTER, NOT A BOOLEAN ────────────────────────────────────────────────
 *
 * React 18 StrictMode mounts, unmounts and remounts effects in development. A boolean would
 * be left false by the throwaway unmount while the real mount still needs it true. Counting
 * claimants is correct under double-invocation and under two claimants overlapping during a
 * transition; the chrome hides while the count is above zero.
 */

interface ScreenOwnershipValue {
  /** True while at least one claimant holds the screen. */
  screenOwned: boolean;
  claim: () => void;
  release: () => void;
}

const ScreenOwnershipContext = createContext<ScreenOwnershipValue | null>(null);

export function ScreenOwnershipProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => setClaims(c => c + 1), []);
  const release = useCallback(() => setClaims(c => Math.max(0, c - 1)), []);
  const value = useMemo(
    () => ({ screenOwned: claims > 0, claim, release }),
    [claims, claim, release],
  );
  return <ScreenOwnershipContext.Provider value={value}>{children}</ScreenOwnershipContext.Provider>;
}

/** Read-only: for the chrome deciding whether to render. Safe outside the provider. */
export function useScreenOwned(): boolean {
  return useContext(ScreenOwnershipContext)?.screenOwned ?? false;
}

/**
 * Declare that this component owns the whole screen for as long as it is mounted.
 *
 * `enabled` so a caller can own the screen conditionally without breaking the rules of
 * hooks -- the draft room claims on every viewport, but a caller that only wants it on
 * mobile can pass a flag rather than calling the hook inside a branch.
 */
export function useOwnsScreen(enabled = true): void {
  const ctx = useContext(ScreenOwnershipContext);
  useEffect(() => {
    if (!enabled || !ctx) return;
    ctx.claim();
    return () => ctx.release();
    // claim/release are stable useCallbacks; ctx identity changes with the count, which must
    // NOT re-run this effect or it would release and re-claim on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
